/**
 * GrandLivre.jsx
 * Affichage par compte avec mouvements + filtres
 * Theme : fond blanc - accent #009688
 *
 * Filtres disponibles :
 *   - Classe de compte (1 à 7)
 *   - Recherche par intitulé ou numéro
 *   - Nature du solde (Débiteur / Créditeur / Tous)
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useGetGrandLivreQuery } from 'actions/comptabilite/queries';
import { exportGrandLivreExcel, exportGrandLivrePdf, comparerComptes } from './utils/exportComptaExcel';
import {
  exportButtonGroupStyle,
  exportExcelButtonStyle,
  exportPdfButtonStyle,
} from './utils/exportButtonStyles';
import TableLoader from 'components/Loader/TableLoader';

// ── Google Fonts ─────────────────────────────────────────────────────
const injectFonts = () => {
  if (document.getElementById('gl-fonts')) return;
  const l = document.createElement('link');
  l.id   = 'gl-fonts';
  l.rel  = 'stylesheet';
  l.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500;600&display=swap';
  document.head.appendChild(l);
};

// ── Tokens ───────────────────────────────────────────────────────────
const C = {
  bg:        '#f0f5f6',
  surface:   '#ffffff',
  border:    '#c8d8db',
  borderSoft:'#e2eced',
  teal:      'rgba(00,96,88,0.8)',
  tealDark:  '#006b78',
  tealLight: 'rgba(00,96,88,0.8)',
  tealDim:   'rgba(0,143,160,0.09)',
  tealStrong:'rgba(0,143,160,0.16)',
  green:     '#1a7a52',
  greenDim:  'rgba(26,122,82,0.10)',
  red:       '#c93535',
  redDim:    'rgba(201,53,53,0.10)',
  blue:      'rgba(00,96,88,0.8)',
  blueDim:   'rgba(31,111,191,0.10)',
  text:      '#0d2226',
  textDim:   '#3d6068',
  textMuted: '#7da5ad',
  white:     '#ffffff',
  mono:      '"DM Mono","Courier New",monospace',
  display:   '"Playfair Display",Georgia,serif',
  sans:      '"DM Sans",system-ui,sans-serif',
};

// ── Utilitaires ──────────────────────────────────────────────────────
const fmt = (n) =>
  n && n !== 0 ? new Intl.NumberFormat('fr-FR').format(n) + '\u00a0F' : '-';

// ── Normalisation de la réponse API ──────────────────────────────────
function normalizeGrandLivre(raw) {
  const empty = { comptes: [], total_debits: 0, total_credits: 0, equilibre: true };
  if (!raw) return empty;

  // Nouveau format : { comptes: [...] }
  if (Array.isArray(raw.comptes)) {
    return {
      comptes:       raw.comptes,
      total_debits:  raw.total_debits  ?? 0,
      total_credits: raw.total_credits ?? 0,
      equilibre:     raw.equilibre     ?? true,
    };
  }

  // Ancien format : { "grand-livre": [...] }
  const flat = raw['grand-livre'] ?? [];
  const map  = {};
  flat.forEach((e) => {
    const k = e.numero_comptable;
    if (!map[k]) {
      map[k] = {
        numero_comptable: k,
        intitule:         e.libelle || ('Compte ' + k),
        classe:           String(k)[0],
        classe_label:     'Classe ' + String(k)[0],
        mouvements:       [],
        total_debit:      0,
        total_credit:     0,
        solde_debiteur:   0,
        solde_crediteur:  0,
        nature_solde:     'NUL',
      };
    }
    map[k].mouvements.push({ date:'', libelle: e.libelle || '', contrepartie:'', debit: e.debit || 0, credit: e.credit || 0 });
    map[k].total_debit  += e.debit  || 0;
    map[k].total_credit += e.credit || 0;
  });
  const comptes = Object.values(map).map((c) => {
    const sd = Math.max(0, c.total_debit - c.total_credit);
    const sc = Math.max(0, c.total_credit - c.total_debit);
    return { ...c, solde_debiteur: sd, solde_crediteur: sc, nature_solde: sd > 0 ? 'D' : sc > 0 ? 'C' : 'NUL' };
  });
  return { comptes, total_debits: raw.total_debits ?? 0, total_credits: raw.total_credits ?? 0, equilibre: true };
}

// ── Styles ───────────────────────────────────────────────────────────
const thStyle = (align = 'left', extra = {}) => ({
  fontFamily:      C.mono,
  fontSize:        '10px',
  fontWeight:      600,
  letterSpacing:   '1.5px',
  textTransform:   'uppercase',
  color:           C.white,
  backgroundColor: C.teal,
  padding:         '10px 12px',
  textAlign:       align,
  borderBottom:    '2px solid ' + C.tealDark,
  whiteSpace:      'nowrap',
  ...extra,
});

const S = {
  wrapper: { fontFamily:C.sans, background:C.bg, color:C.text, padding:'28px 20px 60px', minHeight:'100vh' },
  inner:   { maxWidth:'1160px', margin:'0 auto' },

  // Header
  headerRow: { display:'flex', alignItems:'center', gap:'14px', marginBottom:'6px' },
  title:     { fontFamily:C.display, fontSize:'1.6rem', fontWeight:700, color:C.text, margin:0 },
  badge: {
    fontFamily:C.mono, fontSize:'9px', letterSpacing:'2px', textTransform:'uppercase',
    color:C.teal, background:C.tealDim,
    border:'1px solid rgba(0,143,160,0.25)', padding:'3px 12px', borderRadius:'100px',
  },
  divider: { height:'1px', background:'linear-gradient(90deg,'+C.border+',transparent)', marginTop:'8px', marginBottom:'20px' },

  // Equilibre
  equilibre: (ok) => ({
    display:'inline-flex', alignItems:'center', gap:'8px',
    fontFamily:C.mono, fontSize:'11px', letterSpacing:'1px',
    color: ok ? C.green : C.red,
    background: ok ? C.greenDim : C.redDim,
    border:'1px solid '+(ok ? 'rgba(26,122,82,0.3)' : 'rgba(201,53,53,0.3)'),
    padding:'5px 14px', borderRadius:'100px', marginBottom:'16px',
  }),

  // Barre de filtres
  filterBar: {
    display:       'flex',
    flexWrap:      'wrap',
    gap:           '10px',
    alignItems:    'center',
    background:    C.surface,
    border:        '1px solid ' + C.border,
    borderRadius:  '10px',
    padding:       '14px 18px',
    marginBottom:  '24px',
    boxShadow:     '0 1px 6px rgba(0,143,160,0.06)',
  },
  filterLabel: { fontFamily:C.mono, fontSize:'9px', letterSpacing:'2px', textTransform:'uppercase', color:C.textMuted, marginRight:'2px' },
  filterInput: {
    fontFamily:   C.sans, fontSize:'13px', color:C.text,
    background:   C.bg, border:'1px solid ' + C.border,
    borderRadius: '6px', padding:'6px 10px', outline:'none',
    transition:   'border-color .2s',
  },
  filterSelect: {
    fontFamily:   C.sans, fontSize:'12px', color:C.text,
    background:   C.bg, border:'1px solid ' + C.border,
    borderRadius: '6px', padding:'6px 10px', outline:'none', cursor:'pointer',
  },
  resetBtn: {
    fontFamily:   C.mono, fontSize:'10px', letterSpacing:'1px', textTransform:'uppercase',
    color:        C.teal, background:'transparent',
    border:       '1px solid rgba(0,143,160,0.3)',
    borderRadius: '6px', padding:'6px 12px', cursor:'pointer', transition:'all .2s',
    marginLeft:   'auto',
  },
  countBadge: {
    fontFamily:C.mono, fontSize:'10px', color:C.textMuted,
    background:C.tealDim, border:'1px solid rgba(0,143,160,0.2)',
    padding:'3px 10px', borderRadius:'100px',
  },

  // Grille de cartes
  grid: { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(520px, 1fr))', gap:'18px', marginBottom:'32px' },

  // Carte compte
  card: {
    background:   C.surface, border:'1px solid ' + C.border,
    borderRadius: '10px', overflow:'hidden',
    boxShadow:    '0 2px 14px rgba(0,143,160,0.07)',
    transition:   'box-shadow .2s',
  },
  cardHeader: {
    display:        'flex', alignItems:'center', justifyContent:'space-between',
    padding:        '12px 16px',
    backgroundColor: C.teal,
    borderBottom:   '2px solid ' + C.tealDark,
  },
  cardLeft:   { display:'flex', flexDirection:'column', gap:'2px' },
  cardTitle:  { fontFamily:C.display, fontWeight:700, fontSize:'14px', color:C.white },
  cardClass:  { fontFamily:C.mono, fontSize:'9px', letterSpacing:'1px', color:'rgba(255,255,255,0.65)' },
  cardNum: {
    fontFamily:C.mono, fontSize:'11px', color:C.teal,
    background:C.white, border:'none',
    padding:'3px 10px', borderRadius:'100px', fontWeight:600,
  },

  // Tableau interne
  table:  { width:'100%', borderCollapse:'collapse', fontSize:'12px' },
  tableWrap: {
    overflowX:'auto',
    WebkitOverflowScrolling:'touch',
  },
  TD: (extra = {}) => ({
    padding:'8px 12px', verticalAlign:'middle',
    borderBottom:'1px solid ' + C.borderSoft,
    lineHeight:1.4, ...extra,
  }),

  amtD:  { fontFamily:C.mono, fontWeight:600, color:C.green,   fontSize:'12px' },
  amtC:  { fontFamily:C.mono, fontWeight:600, color:C.red,     fontSize:'12px' },
  dash:  { fontFamily:C.mono, color:C.textMuted, fontSize:'11px' },
  cpte:  { fontFamily:C.mono, fontSize:'10px', color:C.textDim },

  // Ligne solde
  soldeRow: { backgroundColor:'rgba(0,143,160,0.06)', borderTop:'2px solid rgba(0,143,160,0.2)' },
  soldeTd:  { padding:'9px 12px', fontFamily:C.mono, fontSize:'11px', fontWeight:700, color:C.teal },

  badgeD: {
    display:'inline-flex', alignItems:'center', gap:'4px',
    fontFamily:C.mono, fontSize:'9px', letterSpacing:'1px', textTransform:'uppercase',
    color:C.green, background:C.greenDim, border:'1px solid rgba(26,122,82,0.3)',
    padding:'2px 8px', borderRadius:'4px',
  },
  badgeC: {
    display:'inline-flex', alignItems:'center', gap:'4px',
    fontFamily:C.mono, fontSize:'9px', letterSpacing:'1px', textTransform:'uppercase',
    color:C.red, background:C.redDim, border:'1px solid rgba(201,53,53,0.3)',
    padding:'2px 8px', borderRadius:'4px',
  },
  badgeN: {
    display:'inline-flex', alignItems:'center', gap:'4px',
    fontFamily:C.mono, fontSize:'9px', letterSpacing:'1px', textTransform:'uppercase',
    color:C.textMuted, background:C.tealDim, border:'1px solid ' + C.border,
    padding:'2px 8px', borderRadius:'4px',
  },

  // Bouton voir plus
  moreBtn: {
    fontFamily:C.mono, fontSize:'9px', letterSpacing:'1.5px', textTransform:'uppercase',
    color:C.teal, background:C.tealDim, border:'1px solid rgba(0,143,160,0.25)',
    padding:'4px 14px', borderRadius:'100px', cursor:'pointer', display:'block', margin:'6px auto',
  },

  // Total global
  totalCard: {
    background:   C.surface, border:'1px solid rgba(0,143,160,0.3)',
    borderRadius: '10px', overflow:'hidden',
    boxShadow:    '0 2px 14px rgba(0,143,160,0.1)',
  },
  totalHeader: {
    backgroundColor: C.teal, padding:'10px 16px',
    fontFamily:C.mono, fontSize:'10px', letterSpacing:'2px',
    textTransform:'uppercase', color:C.white, fontWeight:600,
  },
  totalRow:   { backgroundColor:'#009688' },
  totalLabel: { padding:'12px 16px', fontFamily:C.mono, fontSize:'10px', letterSpacing:'1px', color:C.teal },
  totalAmt:   { padding:'12px 16px', fontFamily:C.mono, fontSize:'13px', fontWeight:700, color:C.teal, textAlign:'right' },

  loader: { textAlign:'center', padding:'60px', fontFamily:C.mono, fontSize:'12px', color:C.textMuted, letterSpacing:'2px' },
  empty:  { textAlign:'center', padding:'40px', fontFamily:C.mono, fontSize:'12px', color:C.textMuted, letterSpacing:'1px' },
  pagination: {
    display:'flex',
    justifyContent:'space-between',
    alignItems:'center',
    marginTop:'10px',
    marginBottom:'24px',
    gap:'8px',
    flexWrap:'wrap',
  },
  pageBtn: {
    border:'1px solid ' + C.border,
    background:C.surface,
    borderRadius:'6px',
    padding:'6px 10px',
    cursor:'pointer',
    fontFamily:C.mono,
    fontSize:'11px',
  },
};

// ── Carte d'un compte ─────────────────────────────────────────────────
function CompteCard({ compte }) {
  const [expanded, setExpanded] = useState(false);
  const TRUNC      = 4;
  const mvts       = Array.isArray(compte.mouvements) ? compte.mouvements : [];
  const toShow     = expanded ? mvts : mvts.slice(0, TRUNC);
  const hasMore    = mvts.length > TRUNC;
  const nature     = compte.nature_solde ?? 'NUL';
  const montant    = nature === 'D' ? compte.solde_debiteur : nature === 'C' ? compte.solde_crediteur : 0;

  const badgeSolde = nature === 'D'
    ? <span style={S.badgeD}>&#9650; Solde Débiteur</span>
    : nature === 'C'
    ? <span style={S.badgeC}>&#9660; Solde Créditeur</span>
    : <span style={S.badgeN}>Soldé</span>;

  return (
    <div style={S.card}>
      {/* En-tête de la carte */}
      <div style={S.cardHeader}>
        <div style={S.cardLeft}>
          <span style={S.cardTitle}>{compte.intitule}</span>
          <span style={S.cardClass}>{compte.classe_label}</span>
        </div>
        <span style={S.cardNum}>{compte.numero_comptable}</span>
      </div>

      {/* Tableau des mouvements */}
      <div style={S.tableWrap}>
      <table style={{ ...S.table, minWidth:'760px' }}>
        <thead>
          <tr>
            <th style={thStyle('left',  { fontSize:'9px', padding:'8px 12px', backgroundColor:'#f0f5f6', color:C.teal, borderBottom:'1px solid '+C.border })}>Date</th>
            <th style={thStyle('left',  { fontSize:'9px', padding:'8px 12px', backgroundColor:'#f0f5f6', color:C.teal, borderBottom:'1px solid '+C.border })}>Libellé</th>
            <th style={thStyle('left',  { fontSize:'9px', padding:'8px 12px', backgroundColor:'#f0f5f6', color:C.teal, borderBottom:'1px solid '+C.border })}>Contrepartie</th>
            <th style={thStyle('right', { fontSize:'9px', padding:'8px 12px', backgroundColor:'#f0f5f6', color:C.teal, borderBottom:'1px solid '+C.border })}>Débit</th>
            <th style={thStyle('right', { fontSize:'9px', padding:'8px 12px', backgroundColor:'#f0f5f6', color:C.teal, borderBottom:'1px solid '+C.border })}>Crédit</th>
          </tr>
        </thead>
        <tbody>
          {mvts.length === 0 ? (
            <tr><td colSpan={6} style={S.empty}>Aucun mouvement</td></tr>
          ) : (
            toShow.map((mv, i) => (
              <MouvementRow key={i} mv={mv} />
            ))
          )}

          {/* Bouton voir plus */}
          {hasMore && !expanded && (
            <tr>
              <td colSpan={6} style={{ ...S.TD(), borderBottom:'none', padding:'4px' }}>
                <button style={S.moreBtn} onClick={() => setExpanded(true)}>
                  + {mvts.length - TRUNC} mouvement{mvts.length - TRUNC > 1 ? 's' : ''}
                </button>
              </td>
            </tr>
          )}
          {hasMore && expanded && (
            <tr>
              <td colSpan={6} style={{ ...S.TD(), borderBottom:'none', padding:'4px' }}>
                <button style={S.moreBtn} onClick={() => setExpanded(false)}>Réduire</button>
              </td>
            </tr>
          )}

          {/* Ligne totaux du compte */}
          <tr style={{ backgroundColor:'#009688', borderTop:'1px solid rgba(0,143,160,0.15)' }}>
            <td colSpan={3} style={{ ...S.TD({ borderBottom:'none' }), fontFamily:C.mono, fontSize:'9px', letterSpacing:'1px', color:C.textDim, textTransform:'uppercase' }}>
              Total mouvements
            </td>
            <td style={{ ...S.TD({ textAlign:'right', borderBottom:'none' }), fontFamily:C.mono, fontWeight:600, color:C.green, fontSize:'12px' }}>
              {fmt(compte.total_debit)}
            </td>
            <td style={{ ...S.TD({ textAlign:'right', borderBottom:'none' }), fontFamily:C.mono, fontWeight:600, color:C.red, fontSize:'12px' }}>
              {fmt(compte.total_credit)}
            </td>
          </tr>

          {/* Ligne de solde */}
          <tr style={S.soldeRow}>
            <td colSpan={3} style={{ ...S.soldeTd }}>{badgeSolde}</td>
            <td style={{ ...S.soldeTd, textAlign:'right' }}>
              {nature === 'D' ? fmt(montant) : '-'}
            </td>
            <td style={{ ...S.soldeTd, textAlign:'right' }}>
              {nature === 'C' ? fmt(montant) : '-'}
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ── Ligne de mouvement ───────────────────────────────────────────────
function MouvementRow({ mv }) {
  const [hov, setHov] = useState(false);
  if (!mv) return null;

  return (
    <tr
      style={{ background: hov ? 'rgba(0,143,160,0.03)' : C.surface, transition:'background .15s' }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      <td style={S.TD({ fontFamily:C.mono, fontSize:'11px', color:C.textDim, whiteSpace:'nowrap', width:'90px' })}>
        {mv.date || '-'}
      </td>
      <td style={S.TD({ maxWidth:'160px' })}>
        <span style={{ fontSize:'12px', color:C.text }}>{mv.libelle || '-'}</span>
      </td>
      <td style={S.TD({ maxWidth:'180px' })}>
        <span style={S.cpte}>{mv.contrepartie || '-'}</span>
      </td>
      <td style={S.TD({ textAlign:'right', width:'110px' })}>
        {mv.debit > 0
          ? <span style={S.amtD}>{fmt(mv.debit)}</span>
          : <span style={S.dash}>-</span>
        }
      </td>
      <td style={S.TD({ textAlign:'right', width:'110px' })}>
        {mv.credit > 0
          ? <span style={S.amtC}>{fmt(mv.credit)}</span>
          : <span style={S.dash}>-</span>
        }
      </td>
    </tr>
  );
}

// ── Composant principal ──────────────────────────────────────────────
const GrandLivre = ({ dossier_id, date_debut, date_fin }) => {
  useEffect(() => { injectFonts(); }, []);

  // Filtres
  const [search,      setSearch]      = useState('');
  const [filterClasse, setFilterClasse] = useState('');
  const [filterSolde,  setFilterSolde]  = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 6;

  const { data: raw, isLoading, isFetching, refetch } = useGetGrandLivreQuery(
    { dossier_id, date_debut, date_fin },
    { skip: !dossier_id }
  );

  useEffect(() => {
    if (dossier_id) refetch();
  }, [dossier_id, date_debut, date_fin, refetch]);
  useEffect(() => {
    const handleRefresh = () => refetch();
    window.addEventListener("compta:refresh", handleRefresh);
    return () => window.removeEventListener("compta:refresh", handleRefresh);
  }, [refetch]);

  const { comptes, total_debits, total_credits, equilibre } = useMemo(
    () => normalizeGrandLivre(raw),
    [raw]
  );
  const handleExportExcel = () => {
    exportGrandLivreExcel({
      comptes,
      totalDebits: total_debits,
      totalCredits: total_credits,
      equilibre,
      dossierId: dossier_id,
    });
  };
  const handleExportPdf = () => {
    exportGrandLivrePdf({
      comptes,
      totalDebits: total_debits,
      totalCredits: total_credits,
      equilibre,
      dossierId: dossier_id,
    });
  };

  // Filtrage + tri numérique croissant garanti
  const comptesFiltres = useMemo(() => {
    return comptes
      .filter((c) => {
        const matchSearch = !search.trim() || (
          c.intitule.toLowerCase().includes(search.toLowerCase()) ||
          String(c.numero_comptable).includes(search)
        );
        const matchClasse = !filterClasse || c.classe === filterClasse;
        const matchSolde  = !filterSolde  || c.nature_solde === filterSolde;
        return matchSearch && matchClasse && matchSolde;
      })
      .sort((a, b) => comparerComptes(a.numero_comptable, b.numero_comptable));
  }, [comptes, search, filterClasse, filterSolde]);

  // Classes disponibles
  const classesDisponibles = useMemo(() => {
    const set = new Set(comptes.map((c) => c.classe).filter(Boolean));
    return Array.from(set).sort();
  }, [comptes]);

  const hasFilters = search || filterClasse || filterSolde;
  const totalPages = Math.max(1, Math.ceil(comptesFiltres.length / PAGE_SIZE));
  const comptesPage = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return comptesFiltres.slice(start, start + PAGE_SIZE);
  }, [comptesFiltres, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterClasse, filterSolde, comptes.length]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  if (isLoading || isFetching) {
    return (
      <div style={S.wrapper}>
        <div style={S.inner}>
          <div style={{ ...S.card, padding: '30px' }}>
            <TableLoader
              message="Chargement du grand livre..."
              subMessage="Agrégation des comptes et calcul des soldes"
              minHeight="350px"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.wrapper}>
      <div style={S.inner}>

        {/* Titre */}
        <div style={S.headerRow} className="compta-header-row">
          <div className="compta-title-wrap" style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
            <h2 style={S.title}>Grand Livre</h2>
            <span style={S.badge}>PAR COMPTE</span>
          </div>
          <div style={exportButtonGroupStyle} className="compta-export-group">
            <button type="button" onClick={handleExportExcel} style={exportExcelButtonStyle}>
              Export Excel
            </button>
            <button type="button" onClick={handleExportPdf} style={exportPdfButtonStyle}>
              Export PDF
            </button>
          </div>
        </div>
        <div style={S.divider} />

        {/* Équilibre */}
        {comptes.length > 0 && (
          <div style={S.equilibre(equilibre)}>
            <span>{equilibre ? '✓' : '✗'}</span>
            {equilibre ? 'Partie double équilibrée' : 'DÉSÉQUILIBRE - vérifiez les comptes'}
            <span style={{ color:'rgba(0,0,0,0.35)', marginLeft:'8px' }}>
              {new Intl.NumberFormat('fr-FR').format(total_debits) + ' F  /  ' + new Intl.NumberFormat('fr-FR').format(total_credits) + ' F'}
            </span>
          </div>
        )}

        {/* ── Barre de filtres ── */}
        <div style={S.filterBar}>
          {/* Recherche */}
          <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
            <span style={S.filterLabel}>Recherche</span>
            <input
              style={S.filterInput}
              placeholder="Intitulé ou N° compte..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Classe */}
          <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
            <span style={S.filterLabel}>Classe</span>
            <select style={S.filterSelect} value={filterClasse} onChange={(e) => setFilterClasse(e.target.value)}>
              <option value="">Toutes</option>
              {classesDisponibles.map((cls) => (
                <option key={cls} value={cls}>Classe {cls}</option>
              ))}
            </select>
          </div>

          {/* Nature solde */}
          <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
            <span style={S.filterLabel}>Solde</span>
            <select style={S.filterSelect} value={filterSolde} onChange={(e) => setFilterSolde(e.target.value)}>
              <option value="">Tous</option>
              <option value="D">Débiteur</option>
              <option value="C">Créditeur</option>
              <option value="NUL">Nul</option>
            </select>
          </div>

          {/* Compteur + Reset */}
          <span style={S.countBadge}>
            {comptesFiltres.length} compte{comptesFiltres.length > 1 ? 's' : ''}
            {hasFilters ? ' / ' + comptes.length : ''}
          </span>

          {hasFilters && (
            <button
              style={S.resetBtn}
              onClick={() => { setSearch(''); setFilterClasse(''); setFilterSolde(''); }}
            >
              Réinitialiser
            </button>
          )}
        </div>

        {/* ── Grille de cartes ── */}
        {comptesFiltres.length === 0 ? (
          <div style={{ ...S.empty, background:C.surface, borderRadius:'10px', border:'1px solid '+C.border }}>
            AUCUN COMPTE TROUVÉ
          </div>
        ) : (
          <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
          <div style={{ ...S.grid, minWidth:'520px' }}>
            {comptesPage.map((compte) => (
              <CompteCard key={compte.numero_comptable} compte={compte} />
            ))}
          </div>
          </div>
        )}

        {comptesFiltres.length > 0 && (
          <div style={S.pagination}>
            <span style={{ fontFamily:C.mono, fontSize:'11px', color:C.textDim }}>
              Page {currentPage} / {totalPages}
            </span>
            <div style={{ display:'flex', gap:'8px' }}>
              <button
                type="button"
                style={S.pageBtn}
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Précédent
              </button>
              <button
                type="button"
                style={S.pageBtn}
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                Suivant
              </button>
            </div>
          </div>
        )}

        {/* ── Totaux généraux ── */}
        {comptes.length > 0 && (
          <div style={S.totalCard}>
            <div style={S.totalHeader}>Totaux Généraux - Grand Livre</div>
            <div style={S.tableWrap}>
            <table style={{ width:'100%', minWidth:'600px', borderCollapse:'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle('left',  { fontSize:'9px', padding:'9px 16px', backgroundColor:'#f0f5f6', color:C.teal, borderBottom:'1px solid '+C.border })}>Comptes</th>
                  <th style={thStyle('right', { fontSize:'9px', padding:'9px 16px', backgroundColor:'#f0f5f6', color:C.teal, borderBottom:'1px solid '+C.border })}>Total Débits</th>
                  <th style={thStyle('right', { fontSize:'9px', padding:'9px 16px', backgroundColor:'#f0f5f6', color:C.teal, borderBottom:'1px solid '+C.border })}>Total Crédits</th>
                </tr>
              </thead>
              <tbody>
                <tr style={S.totalRow}>
                  <td style={S.totalLabel}>{comptes.length} compte{comptes.length > 1 ? 's' : ''}</td>
                  <td style={{ ...S.totalAmt, color:C.green }}>{new Intl.NumberFormat('fr-FR').format(total_debits)} F</td>
                  <td style={{ ...S.totalAmt, color:C.red }}>{new Intl.NumberFormat('fr-FR').format(total_credits)} F</td>
                </tr>
              </tbody>
            </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default GrandLivre;